import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { BellRing, Mail, Timer } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { DueBadge } from '../components/badges';
import { AlertsNote } from '../components/borrower-alerts';
import { ChangeDueDateDialog, CheckInDialog } from '../components/checkout-dialogs';
import { LendLoanerModal } from '../components/loaner-dialog';
import { useToast } from '../components/toast';
import { Alert, Badge, Button, Card, cn, EmptyState, ErrorState, Input, PageHeader, Pagination, Spinner, Table, Tabs, Td, Th } from '../components/ui';
import { api, errorMessage } from '../lib/api';
import { useAuth } from '../lib/auth';
import { daysLate, lateLabel, LOANER_REASON_SHORT, REMINDER_STAGE_LABEL, type CheckoutReminder, type CheckoutSettings, type Loaner, type LoanerPage } from '../lib/checkouts';
import { formatDate, formatDateTime, formatRelative } from '../lib/format';
import { useDebouncedValue, useUrlState } from '../lib/hooks';

const PAGE_SIZE = 25;
const DEFAULTS = { state: 'active', search: '', page: '1' };
type State = 'active' | 'due-soon' | 'overdue' | 'returned' | 'all';

function SummaryTile({
  label,
  value,
  sub,
  tone,
  active,
  onClick,
}: {
  label: string;
  value: number | undefined;
  sub: string;
  tone: 'slate' | 'amber' | 'red' | 'green';
  active: boolean;
  onClick(): void;
}) {
  const tones = { slate: 'text-slate-900', amber: 'text-amber-600', red: 'text-red-600', green: 'text-emerald-600' };
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'rounded-xl border bg-white p-4 text-left shadow-sm transition-shadow hover:shadow-md',
        active ? 'border-brand-400 ring-2 ring-brand-100' : 'border-slate-200',
      )}
    >
      <p className="text-sm font-medium text-slate-500">{label}</p>
      <p className={cn('mt-1 text-3xl font-semibold tracking-tight', value ? tones[tone] : 'text-slate-900')}>{value ?? '–'}</p>
      <p className="mt-1 text-xs text-slate-500">{sub}</p>
    </button>
  );
}

/** The latest reminder that went out, or the latest one that didn't. */
function LastReminder({ reminders }: { reminders: CheckoutReminder[] }) {
  const reminder = reminders.find((row) => row.status !== 'SKIPPED') ?? reminders[0];
  if (!reminder) return <span className="text-slate-400">None yet</span>;
  const label = REMINDER_STAGE_LABEL[reminder.stage];
  return (
    <div title={reminder.error ?? (reminder.toAddress ? `To ${reminder.toAddress}` : undefined)}>
      {reminder.status === 'SENT' ? (
        <Badge tone={reminder.stage === 'ESCALATED' ? 'red' : 'blue'}>
          <Mail className="size-3" />
          {label}
        </Badge>
      ) : reminder.status === 'FAILED' ? (
        <Badge tone="red">{label}: not sent</Badge>
      ) : (
        <Badge>{label}: skipped</Badge>
      )}
      <p className="mt-0.5 text-xs text-slate-500">{formatRelative(reminder.createdAt)}</p>
    </div>
  );
}

export function LoanersPage() {
  const { can } = useAuth();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [filters, setFilters] = useUrlState(DEFAULTS);
  const [searchInput, setSearchInput] = useState(filters.search);
  const search = useDebouncedValue(searchInput, 300);
  const [lending, setLending] = useState(false);
  const [checkingIn, setCheckingIn] = useState<Loaner | null>(null);
  const [changingDue, setChangingDue] = useState<Loaner | null>(null);

  useEffect(() => {
    if (search !== filters.search) setFilters({ search });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const page = Number(filters.page) || 1;
  const list = useQuery({
    queryKey: ['loaners', filters],
    queryFn: () => api.get<LoanerPage>('/loaners', { ...filters, page, pageSize: PAGE_SIZE }),
    placeholderData: keepPreviousData,
    refetchInterval: 60_000,
  });
  const settings = useQuery({ queryKey: ['checkout-settings'], queryFn: () => api.get<CheckoutSettings>('/checkout-settings'), enabled: can('TECHNICIAN') });

  const remind = useMutation({
    mutationFn: (loaner: Loaner) => api.post<CheckoutReminder>(`/checkouts/${loaner.id}/remind`),
    onSuccess: (reminder, loaner) => {
      void queryClient.invalidateQueries({ queryKey: ['loaners'] });
      toast.success(`Emailed ${loaner.assigneeLabel} at ${reminder.toAddress}`);
    },
    onError: (err) => toast.error(errorMessage(err)),
  });

  const state = filters.state as State;
  const summary = list.data?.summary;
  const canWrite = can('TECHNICIAN');
  const returned = state === 'returned';

  return (
    <>
      <PageHeader
        title="Loaners"
        description="Devices lent while someone’s own is repaired, or for the day when they forgot it."
        actions={
          canWrite && (
            <Button variant="primary" icon={<Timer />} onClick={() => setLending(true)}>
              Lend a loaner
            </Button>
          )
        }
      />

      {settings.data && !settings.data.loanerReminders.enabled && (
        <Alert tone="blue" icon={<BellRing />} title="Automatic reminders are off" className="mb-6">
          Borrowers aren’t emailed when a loaner is due or overdue.{' '}
          {can('ADMIN') ? (
            <Link to="/settings?tab=checkouts" className="font-medium underline">
              Turn them on
            </Link>
          ) : (
            'An administrator can turn them on in Settings.'
          )}
        </Alert>
      )}

      <div className="mb-6 grid grid-cols-2 gap-4 lg:grid-cols-4">
        <SummaryTile
          label="Out now"
          value={summary?.active}
          sub={summary ? `${summary.available} of ${summary.pool} on the shelf` : 'Loading…'}
          tone="slate"
          active={state === 'active'}
          onClick={() => setFilters({ state: 'active' })}
        />
        <SummaryTile label="Due soon" value={summary?.dueSoon} sub="By the end of tomorrow" tone="amber" active={state === 'due-soon'} onClick={() => setFilters({ state: 'due-soon' })} />
        <SummaryTile
          label="Overdue"
          value={summary?.overdue}
          sub={summary?.escalated ? `${summary.escalated} sent the last reminder` : 'Past their due date'}
          tone="red"
          active={state === 'overdue'}
          onClick={() => setFilters({ state: 'overdue' })}
        />
        <SummaryTile label="Returned this week" value={summary?.returnedThisWeek} sub="In the last 7 days" tone="green" active={returned} onClick={() => setFilters({ state: 'returned' })} />
      </div>

      <Tabs<State>
        className="mb-4"
        value={state}
        onChange={(value) => setFilters({ state: value })}
        tabs={[
          { value: 'active', label: 'Out', count: summary?.active },
          { value: 'due-soon', label: 'Due soon', count: summary?.dueSoon },
          { value: 'overdue', label: 'Overdue', count: summary?.overdue },
          { value: 'returned', label: 'Returned' },
          { value: 'all', label: 'All' },
        ]}
      />

      <Card>
        <div className="flex flex-wrap items-center gap-2 border-b border-slate-100 p-3">
          <Input value={searchInput} onChange={(event) => setSearchInput(event.target.value)} placeholder="Search borrower or asset tag" className="w-full sm:w-72" />
          {list.isFetching && !list.isLoading && <Spinner className="size-4" />}
        </div>

        {list.isLoading ? (
          <div className="flex h-60 items-center justify-center">
            <Spinner />
          </div>
        ) : list.error ? (
          <ErrorState error={list.error} onRetry={() => list.refetch()} />
        ) : list.data!.items.length === 0 ? (
          <EmptyState
            icon={<Timer />}
            title={state === 'overdue' ? 'No loaners are overdue' : state === 'due-soon' ? 'Nothing is due soon' : 'No loaners here'}
            description={summary?.pool === 0 ? 'Put devices in the loaner pool by editing them and checking “In the loaner pool.”' : undefined}
          />
        ) : (
          <>
            <Table>
              <thead>
                <tr>
                  <Th>Loaner</Th>
                  <Th>Borrower</Th>
                  <Th>Why</Th>
                  <Th>Lent</Th>
                  <Th>{returned ? 'Returned' : 'Due'}</Th>
                  {!returned && <Th>Reminders</Th>}
                  {canWrite && !returned && <Th className="text-right">Actions</Th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 bg-white">
                {list.data!.items.map((loaner) => {
                  const late = daysLate(loaner);
                  return (
                    <tr key={loaner.id} className="hover:bg-slate-50/60">
                      <Td className="max-w-xs">
                        <div className="flex items-center gap-1.5">
                          <Link to={`/assets/${loaner.asset.id}`} className="font-mono text-xs font-semibold text-brand-800 hover:underline">
                            {loaner.asset.assetTag}
                          </Link>
                          <AlertsNote value={loaner.alertsAtCheckout} />
                        </div>
                        <p className="truncate text-xs text-slate-500">{loaner.asset.name}</p>
                      </Td>
                      <Td className="max-w-xs">
                        {loaner.personId ? (
                          <Link to={`/people/${loaner.personId}`} className="font-medium text-slate-900 hover:text-brand-700">
                            {loaner.assigneeLabel}
                          </Link>
                        ) : (
                          <span className="font-medium text-slate-900">{loaner.assigneeLabel}</span>
                        )}
                        <p className="truncate text-xs text-slate-500">{loaner.person?.email ?? 'No email address'}</p>
                      </Td>
                      <Td className="whitespace-nowrap">
                        {loaner.loanerReason ? LOANER_REASON_SHORT[loaner.loanerReason] : '—'}
                        {loaner.workOrder && (
                          <p className="text-xs">
                            <Link to={`/work-orders/${loaner.workOrder.id}`} className="text-brand-700 hover:underline">
                              Ticket #{loaner.workOrder.number}
                            </Link>
                          </p>
                        )}
                      </Td>
                      <Td className="whitespace-nowrap">
                        {formatDate(loaner.checkedOutAt)}
                        <p className="text-xs text-slate-500">{loaner.checkedOutBy?.name}</p>
                      </Td>
                      <Td className="whitespace-nowrap">
                        {loaner.checkedInAt ? (
                          <>
                            {formatDate(loaner.checkedInAt)}
                            <div className="mt-0.5">{late !== null ? <Badge tone="amber">{lateLabel(late)}</Badge> : <span className="text-xs text-slate-500">On time</span>}</div>
                          </>
                        ) : (
                          <>
                            <DueBadge dueAt={loaner.dueAt} />
                            {loaner.dueAt && <p className="mt-0.5 text-xs text-slate-500">{formatDateTime(loaner.dueAt)}</p>}
                          </>
                        )}
                      </Td>
                      {!returned && (
                        <Td className="whitespace-nowrap">{loaner.checkedInAt ? <span className="text-slate-400">—</span> : <LastReminder reminders={loaner.reminders} />}</Td>
                      )}
                      {canWrite && !returned && (
                        <Td className="text-right whitespace-nowrap">
                          {!loaner.checkedInAt && (
                            <div className="flex justify-end gap-1">
                              <Button
                                size="sm"
                                variant="ghost"
                                disabled={!loaner.person?.email}
                                loading={remind.isPending && remind.variables?.id === loaner.id}
                                onClick={() => remind.mutate(loaner)}
                              >
                                Remind
                              </Button>
                              <Button size="sm" variant="ghost" onClick={() => setChangingDue(loaner)}>
                                Change due
                              </Button>
                              <Button size="sm" onClick={() => setCheckingIn(loaner)}>
                                Check in
                              </Button>
                            </div>
                          )}
                        </Td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </Table>
            <Pagination page={page} pageSize={PAGE_SIZE} total={list.data!.total} onPage={(next) => setFilters({ page: String(next) }, { resetPage: false })} />
          </>
        )}
      </Card>

      <LendLoanerModal open={lending} onClose={() => setLending(false)} />
      {checkingIn && <CheckInDialog open onClose={() => setCheckingIn(null)} checkout={checkingIn} />}
      {changingDue && <ChangeDueDateDialog open onClose={() => setChangingDue(null)} checkout={changingDue} />}
    </>
  );
}
